import React, { useRef, useState } from 'react';
import Layout from '../components/Layout';
import SupportContact from '../components/SupportContact';
import Icon from '../components/Icon';
import { ONBOARDING_CALL_URL } from '../utils/externalLinks';

// Check if user is within their first week
const isNewUser = (user) => {
  if (!user?.created_at) return false;
  const createdAt = new Date(user.created_at);
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  return createdAt > oneWeekAgo;
};

function SupportPage({ user, onLogout, darkMode = true, onToggleDarkMode }) {
  const showOnboardingPrompt = isNewUser(user);
  const supportFormRef = useRef(null);
  const [ticketPrefill, setTicketPrefill] = useState(null);

  const handleQuickHelpClick = (prefill) => {
    setTicketPrefill({ ...prefill, _stamp: Date.now() });
    if (supportFormRef.current) {
      supportFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const quickHelpButtonStyle = (background, border) => ({
    padding: '16px',
    background,
    borderRadius: '12px',
    border,
    textAlign: 'left',
    cursor: 'pointer'
  });

  return (
    <Layout
      user={user}
      onLogout={onLogout}
      pageTitle="Support"
      pageSubtitle="Contact and support - reach out if you have any questions or issues"
      darkMode={darkMode}
      onToggleDarkMode={onToggleDarkMode}
    >
      <div className="dashboard-shell" style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Onboarding Call Prompt - for new users within first week */}
        {showOnboardingPrompt && (
          <div
            style={{
              marginBottom: '24px',
              padding: '24px',
              background: 'linear-gradient(135deg, rgba(61, 90, 32, 0.15) 0%, rgba(74, 140, 24, 0.15) 100%)',
              borderRadius: '16px',
              border: '1px solid rgba(61, 90, 32, 0.3)',
              boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.2)' : '0 4px 20px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'rgba(74, 140, 24, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Icon name="calendar" color="brand" darkMode={darkMode} size={24} />
              </div>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <h3
                  style={{
                    margin: '0 0 8px 0',
                    color: darkMode ? '#4A8C18' : '#3d5a20',
                    fontSize: '18px',
                    fontWeight: '700'
                  }}
                >
                  Got questions? Book an onboarding call!
                </h3>
                <p
                  style={{
                    margin: '0 0 16px 0',
                    color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}
                >
                  Get a personalized 30-minute walkthrough with our team. We will help you set up your account,
                  answer your questions, and make sure you are getting the most out of Frame-Book.
                </p>
                <a
                  href={ONBOARDING_CALL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    background: 'var(--lime-primary)',
                    color: '#ffffff',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    boxShadow: '0 4px 12px rgba(61, 90, 32, 0.3)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(61, 90, 32, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(61, 90, 32, 0.3)';
                  }}
                >
                  <Icon name="calendar" customColor="#ffffff" size={16} />
                  Book 30-min onboarding call
                </a>
              </div>
            </div>
          </div>
        )}

        <div ref={supportFormRef}>
          <SupportContact user={user} darkMode={darkMode} prefillTicket={ticketPrefill} />
        </div>

        {/* Additional help section */}
        <div
          style={{
            marginTop: '24px',
            padding: '24px',
            background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
            borderRadius: '16px',
            border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)'
          }}
        >
          <h3
            style={{
              margin: '0 0 16px 0',
              color: darkMode ? 'white' : '#1f2937',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon name="info" color="info" darkMode={darkMode} size={18} />
            Quick help
          </h3>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}
          >
            <button
              type="button"
              onClick={() =>
                handleQuickHelpClick({
                  ticketType: 'account_issue',
                  priority: 'normal',
                  subject: 'Account settings help',
                  description: 'I need help with updating my account information and settings.'
                })
              }
              style={quickHelpButtonStyle(
                darkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                '1px solid rgba(59, 130, 246, 0.2)'
              )}
            >
              <Icon name="phone" color="info" darkMode={darkMode} size={24} style={{ marginBottom: '8px' }} />
              <h4 style={{ margin: '0 0 8px 0', color: darkMode ? 'white' : '#1f2937', fontSize: '14px' }}>
                Account management
              </h4>
              <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>
                You can change your password and profile details under Profile.
              </p>
            </button>

            <button
              type="button"
              onClick={() =>
                handleQuickHelpClick({
                  ticketType: 'service_dispute',
                  priority: 'normal',
                  subject: 'Service-related question',
                  description: 'I have a question about a service and need support guidance.'
                })
              }
              style={quickHelpButtonStyle(
                darkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                '1px solid rgba(16, 185, 129, 0.2)'
              )}
            >
              <Icon name="service" color="success" darkMode={darkMode} size={24} style={{ marginBottom: '8px' }} />
              <h4 style={{ margin: '0 0 8px 0', color: darkMode ? 'white' : '#1f2937', fontSize: '14px' }}>
                Service questions
              </h4>
              <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>
                For service-related questions, contact the service shop directly.
              </p>
            </button>

            <button
              type="button"
              onClick={() =>
                handleQuickHelpClick({
                  ticketType: 'general',
                  priority: 'high',
                  subject: 'Urgent support request',
                  description: 'I need a quicker response regarding my issue.'
                })
              }
              style={quickHelpButtonStyle(
                darkMode ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                '1px solid rgba(245, 158, 11, 0.2)'
              )}
            >
              <Icon name="schedule" color="warning" darkMode={darkMode} size={24} style={{ marginBottom: '8px' }} />
              <h4 style={{ margin: '0 0 8px 0', color: darkMode ? 'white' : '#1f2937', fontSize: '14px' }}>
                Response time
              </h4>
              <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>
                We usually reply within 24 hours. For urgent matters, choose high priority.
              </p>
            </button>

            <button
              type="button"
              onClick={() =>
                handleQuickHelpClick({
                  ticketType: 'bike_block',
                  priority: 'high',
                  subject: 'Blocked bike issue',
                  description: 'My bike appears blocked and I need immediate assistance to resolve it.'
                })
              }
              style={quickHelpButtonStyle(
                darkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.1)',
                '1px solid rgba(139, 92, 246, 0.2)'
              )}
            >
              <Icon name="block" color="error" darkMode={darkMode} size={24} style={{ marginBottom: '8px' }} />
              <h4 style={{ margin: '0 0 8px 0', color: darkMode ? 'white' : '#1f2937', fontSize: '14px' }}>
                Bike blocked?
              </h4>
              <p style={{ margin: 0, fontSize: '13px', color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280' }}>
                If a shop blocked your bike, open a ticket with type "Blocked bike issue".
              </p>
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default SupportPage;
